/**
 * @fileoverview 表格行编辑模态弹窗组件
 * 提供完整的表单编辑功能，支持多种字段类型和实时验证。
 *
 * 主要特性：
 * - 支持多种字段类型：文本、数字、百分比、国籍选择
 * - 实时表单验证，提供即时反馈
 * - 异步提交处理，支持加载状态
 * - 必填字段标识
 * - 支持编辑和新增两种模式
 */

import React from "react";
import { i18nCrossUtil, IS_WEB } from "@adgm/libs";
import type { IColumnConfig, IRowData } from "@adgm/cross-models";
import { FormFieldRenderer } from "./FormFieldRenderer";
import { useRowEditor } from "./useRowEditor";
import { ModalConfirm } from "../../ModalConfirm";
import { colors, fontFamily, fontSize, lineHeight } from "../../../styles/common";
import { KeyboardAwareScrollView, Text, View } from "../../../platformComponents";
import { GenericFormProvider } from "../../../kyc/GenericForm/GenericFormContext";
/**
 * 组件属性接口
 * @description 定义ModalRowEditor组件的所有属性
 */
interface IProps {
  /** 模态弹窗是否可见 */
  visible: boolean;
  /** 编辑的数据，null表示新增模式 */
  editData?: IRowData | null;
  /** 表格列配置数组 */
  columns: IColumnConfig[];
  /** 取消按钮回调 */
  onCancel: () => void;
  /** 提交表单回调 */
  onSubmit: (data: IRowData) => void;
  /** 弹窗标题 */
  title?: string;
  /** 确认按钮文本 */
  okText?: string;
  /** 取消按钮文本 */
  cancelText?: string;
  /** 底部描述文案 */
  descriptionText?: string;
}
const crossIntl = i18nCrossUtil.t();
export const ModalRowEditor: React.FC<IProps> = props => {
  const {
    visible,
    editData,
    columns,
    onCancel,
    onSubmit,
    title,
    okText = crossIntl["shareholder.button.save"],
    descriptionText,
  } = props;

  const { formData, errors, validateForm, updateField, onValidation } = useRowEditor(
    visible,
    editData,
    columns,
  );

  /**
   * 处理表单提交
   * @description 验证表单后异步提交数据，管理加载状态
   */
  const handleSubmit = async () => {
    // 验证所有字段
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <ModalConfirm
      title={IS_WEB ? title || crossIntl["shareholder.modal.title"] : ""}
      style={{ backgroundColor: colors.mainBackground }}
      isVisible={visible}
      onCancelClick={onCancel}
      confirmButtonText={okText}
      onConfirmClick={() => {
        void handleSubmit();
      }}
      onHide={onCancel}
      showClose>
      <GenericFormProvider genericFormType="information">
        <KeyboardAwareScrollView style={{ height: "80%" }}>
          {columns.map((column, index) => {
            // 检查是否需要显示分组标题
            const showGroupTitle =
              column.groupTitle &&
              (index === 0 || columns[index - 1]?.groupTitle !== column.groupTitle);

            return (
              <React.Fragment key={column.key}>
                {showGroupTitle ? (
                  <View style={{ marginBottom: 8 }}>
                    <Text
                      style={{
                        fontSize: IS_WEB ? fontSize.f16 : fontSize.f14,
                        fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonMedium,
                        color: colors.red,
                      }}>
                      *<Text style={{ color: colors.white }}>{` ${column.groupTitle}`}</Text>
                    </Text>
                  </View>
                ) : null}
                <FormFieldRenderer
                  column={column}
                  value={formData[column.dataIndex]}
                  error={errors[column.dataIndex]}
                  onValueChange={value => updateField(column.dataIndex, value)}
                  onValidation={onValidation}
                />
              </React.Fragment>
            );
          })}

          {descriptionText ? (
            <Text
              style={{
                color: "rgba(255, 255, 255, 0.80)",
                fontFamily: IS_WEB ? "Common-Bold" : fontFamily.commonBold,
                fontSize: fontSize.f14,
                fontWeight: "400",
                lineHeight: lineHeight.l16,
              }}>
              {descriptionText}
            </Text>
          ) : null}
        </KeyboardAwareScrollView>
      </GenericFormProvider>
    </ModalConfirm>
  );
};
